import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Slide1 from "./components/Slide1"; // Slide 1
import Slide2 from "./components/Slide2"; // Overview
import Slide3 from "./components/Slide3"; // Mazville
import Slide4 from "./components/Slide4"; // AAN Publications
import Slide5 from "./components/Slide5"; // Lextria
import Slide6 from "./components/Slide6"; // CertGen
import Slide7 from "./components/Slide7"; // CertGen (moved)
import Slide8 from "./components/Slide8"; // Auto IP
import Slide9 from "./components/Slide9"; // Thank you / final slide

export default function PresentationPortfolio() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDirection, setFlipDirection] = useState("forward");
  const [animStage, setAnimStage] = useState("idle"); // 'idle' | 'out' | 'in'
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const slides = [
    {
      component: Slide1,
      gradient: "from-blue-900 via-blue-800 to-indigo-900",
    },
    { component: Slide2, gradient: "from-slate-800 via-gray-700 to-gray-600" },
    {
      component: Slide3,
      gradient: "from-emerald-400 via-teal-500 to-cyan-600",
    },
    {
      component: Slide4,
      gradient: "from-violet-500 via-purple-500 to-fuchsia-500",
    },
    {
      component: Slide5,
      gradient: "from-slate-800 via-slate-700 to-slate-600",
    },
    { component: Slide6, gradient: "from-sky-900 via-sky-800 to-indigo-700" },
    {
      component: Slide7,
      gradient: "from-blue-500 via-indigo-500 to-purple-600",
    },
    { component: Slide8, gradient: "from-gray-500 via-gray-600 to-gray-700" },
    {
      component: Slide9,
      gradient: "from-indigo-900 via-violet-800 to-slate-800",
    },
  ];

  const totalSlides = slides.length;

  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1 && animStage === "idle") {
      setIsFlipping(true);
      setFlipDirection("forward");
      setAnimStage("out");
      setTimeout(() => {
        setCurrentSlide((prev) => prev + 1);
        setAnimStage("in");
      }, 320);
      setTimeout(() => {
        setIsFlipping(false);
        setAnimStage("idle");
      }, 680);
    }
  }, [totalSlides, currentSlide, animStage]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0 && animStage === "idle") {
      setIsFlipping(true);
      setFlipDirection("backward");
      setAnimStage("out");
      setTimeout(() => {
        setCurrentSlide((prev) => prev - 1);
        setAnimStage("in");
      }, 320);
      setTimeout(() => {
        setIsFlipping(false);
        setAnimStage("idle");
      }, 680);
    }
  }, [currentSlide, animStage]);

  const goToSlide = useCallback(
    (index) => {
      if (index !== currentSlide && animStage === "idle") {
        setIsFlipping(true);
        setFlipDirection(index > currentSlide ? "forward" : "backward");
        setAnimStage("out");
        setTimeout(() => {
          setCurrentSlide(index);
          setAnimStage("in");
        }, 320);
        setTimeout(() => {
          setIsFlipping(false);
          setAnimStage("idle");
        }, 680);
      }
    },
    [currentSlide, animStage],
  );

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        nextSlide();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        prevSlide();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide]);

  // Swipe detection
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  const currentGradient =
    slides[currentSlide]?.gradient || "from-gray-900 to-gray-800";

  const slideTransitionStyle = {
    transformStyle: "preserve-3d",
    transition:
      "transform 360ms cubic-bezier(.22,.9,.32,1), opacity 360ms ease",
    transform:
      animStage === "out"
        ? flipDirection === "forward"
          ? "translateX(-30%) scale(0.98)"
          : "translateX(30%) scale(0.98)"
        : animStage === "in"
          ? "translateX(0) scale(1)"
          : "translateX(0) scale(1)",
    opacity: animStage === "out" ? 0 : 1,
    filter: animStage === "out" ? "blur(2px)" : "none",
  };

  return (
    <div
      className={`w-screen overflow-hidden py-4 sm:py-8 md:py-12 lg:py-16 bg-gradient-to-br ${currentGradient} relative transition-all duration-700`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        height: "100vh",
        height: "100dvh",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      {/* Animated Background Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-20 right-20 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="vignette" />
        </div>
      </div>

      {/* Enhanced Logo */}
      <div className="absolute top-3 left-3 md:top-6 md:left-6 z-20">
        <div
          className="w-10 h-10 md:w-16 md:h-16 glass-effect rounded-full border-2 border-white/30 flex items-center justify-center shadow-2xl smooth-hover overflow-hidden"
          style={{
            boxShadow:
              "0 8px 32px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
          }}
        >
          <div className="w-8 h-8 md:w-14 md:h-14 bg-white rounded-full flex items-center justify-center overflow-hidden">
            <img
              src="/logo.png"
              alt="Trinaski Labs"
              className="w-7 h-7 md:w-12 md:h-12 max-w-full max-h-full object-contain"
            />
          </div>
        </div>
      </div>

      {/* Slides Container with Beautiful 3D Page Flip */}
      <div className="w-full h-full flex items-center justify-center perspective-container">
        <div className="relative w-full h-full" style={slideTransitionStyle}>
          <div className="w-full h-full flex items-center justify-center px-3 sm:px-6 md:px-12 lg:px-24">
            {/* Render the current slide component */}
            {slides[currentSlide] &&
              (() => {
                const CurrentSlideComponent = slides[currentSlide].component;
                return <CurrentSlideComponent key={currentSlide} />;
              })()}
          </div>
        </div>
      </div>

      {/* Navigation Arrows with Enhanced Effects */}
      {currentSlide > 0 && (
        <button
          onClick={prevSlide}
          disabled={isFlipping}
          className="absolute left-2 md:left-6 lg:left-10 top-1/2 -translate-y-1/2 w-10 h-10 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-xl md:rounded-2xl glass-effect shadow-2xl flex items-center justify-center smooth-hover disabled:opacity-50 disabled:cursor-not-allowed z-20 border-2 border-white/20"
          aria-label="Previous slide"
          style={{
            boxShadow:
              "0 8px 32px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
          }}
        >
          <ChevronLeft className="text-white drop-shadow-lg" size={20} />
        </button>
      )}

      {currentSlide < totalSlides - 1 && (
        <button
          onClick={nextSlide}
          disabled={isFlipping}
          className="absolute right-2 md:right-6 lg:right-10 top-1/2 -translate-y-1/2 w-10 h-10 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-xl md:rounded-2xl glass-effect shadow-2xl flex items-center justify-center smooth-hover disabled:opacity-50 disabled:cursor-not-allowed z-20 border-2 border-white/20"
          aria-label="Next slide"
          style={{
            boxShadow:
              "0 8px 32px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
          }}
        >
          <ChevronRight className="text-white drop-shadow-lg" size={20} />
        </button>
      )}

      {/* Enhanced Slide Indicators */}
      <div className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 flex gap-2 md:gap-4 z-20">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            disabled={isFlipping}
            className={`transition-all duration-500 rounded-full glass-effect smooth-hover disabled:cursor-not-allowed ${
              index === currentSlide
                ? "w-8 h-2.5 md:w-16 md:h-4 bg-white/80 shadow-lg scale-110"
                : "w-2.5 h-2.5 md:w-4 md:h-4 bg-white/30 hover:bg-white/50 border-2 border-white/40"
            }`}
            style={{
              boxShadow:
                index === currentSlide
                  ? "0 8px 25px rgba(255, 255, 255, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.4)"
                  : "0 4px 12px rgba(255, 255, 255, 0.1)",
            }}
          />
        ))}
      </div>

      {/* Enhanced Slide Counter */}
      <div
        className="absolute top-3 right-3 md:top-6 md:right-6 glass-effect px-3 py-1.5 md:px-6 md:py-3 rounded-xl md:rounded-2xl text-white font-bold text-xs md:text-base border-2 border-white/20 shadow-xl z-20 smooth-hover"
        style={{
          boxShadow:
            "0 8px 32px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
        }}
      >
        <span className="drop-shadow-md">{currentSlide + 1}</span>
        <span className="text-white/60 mx-1 md:mx-2">of</span>
        <span className="drop-shadow-md">{totalSlides}</span>
      </div>
    </div>
  );
}
